import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  LayoutDashboard,
  Building2,
  BriefcaseBusiness,
  Megaphone,
  BarChart3,
  Users,
  ClipboardList,
  CreditCard,
  Settings,
  FileText,
  FileCheck,
  Bell,
  Search,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Eye,
  Send,
  MapPin,
  Navigation,
  UserRound,
  Languages,
  CalendarDays,
  Clock3,
  Target,
  MousePointerClick,
  Heart,
  Share2,
  MessageCircle,
  Phone,
  CheckCircle2,
  Edit3,
  Image as ImageIcon,
  Crown,
  Smartphone,
  Tablet,
  Monitor,
  UsersRound,
  Activity,
  BarChart4,
  HandCoins,
  X,
  Save,
  XCircle,
} from "lucide-react";
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  CircularProgress,
} from "@mui/material";
import { MyLocation } from "@mui/icons-material";
import {
  useJsApiLoader,
  GoogleMap,
  Marker,
  Autocomplete as GoogleAutocomplete,
} from "@react-google-maps/api";
import axios from "axios";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { BASE_URL, GOOGLEMAPS_API_KEY } from "../../Api/ApiUrls";
import "./AdvertisementWizard.css";

const GOOGLE_MAPS_API_KEY = GOOGLEMAPS_API_KEY;

const containerStyle = {
  width: "100%",
  height: "300px",
  borderRadius: "8px",
  overflow: "hidden",
};

const centerDefault = {
  lat: 18.387449,
  lng: 78.803236,
};

const libraries = ["places"];

const propertyImages = [
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=700&q=85",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=700&q=85",
  "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=700&q=85",
  "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=700&q=85",
];

const steps = [
  "Ad Details",
  "Campaign",
  "Ad Creative",
  "Ad Placements",
  "Targeting",
  "Review & Publish",
];

const navItems = [
  [LayoutDashboard, "Dashboard"],
  [Building2, "Properties"],
  [BriefcaseBusiness, "Projects"],
  [Megaphone, "Advertisements", true],
  [BarChart3, "Campaigns"],
  [Users, "Leads"],
  [Users, "Users"],
  [Building2, "Builders"],
  [ClipboardList, "Enquiries"],
  [BarChart3, "Reports"],
  [CreditCard, "Payments"],
  [Settings, "Settings"],
  [FileText, "Content Management"],
  [FileCheck, "System Logs"],
];

// Mapping for placement IDs to display panel names
const placementIdToPanelName = {
  "new-launch-nearest": "new_launch_nearest",
  "new-launch-similar": "new_launch_similar",
  "premium-nearest": "premium_nearest",
  "premium-similar": "premium_similar",
  "recommended-nearest": "recommended_nearest",
  "recommended-similar": "recommended_similar",
  "featured-nearest": "featured_nearest",
  "featured-similar": "featured_similar",
  "buy-view": "buy_view",
  "buy-list": "buy_list",
  "buy-description": "buy_description",
  "rent-view": "rent_view",
  "rent-list": "rent_list",
  "rent-description": "rent_description",
  "lease-view": "lease_view",
  "lease-list": "lease_list",
  "lease-description": "lease_description",
  "jv-view": "jv_view",
  "jv-list": "jv_list",
  "jv-description": "jv_description",
  "action-auction": "action_auction",
  "action-list": "action_list",
  "action-description": "action_description",
};

// Priority mapping between UI (standard/high/premium) and backend (low/medium/high)
const PRIORITY_TO_BACKEND = {
  standard: "low",
  high: "medium",
  premium: "high",
};

const PRIORITY_FROM_BACKEND = {
  low: "standard",
  medium: "high",
  high: "premium",
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function Sidebar() {
  return (
    <aside className="adv-sidebar">
      <div className="adv-brand">
        <div className="adv-brand-mark">♜</div>
        <div>
          <div className="adv-brand-name">JD/JV</div>
          <div className="adv-brand-sub">PROPERTIES</div>
        </div>
      </div>

      <div className="adv-nav">
        {navItems.map(([Icon, label, active]) => (
          <React.Fragment key={label}>
            <div className={`adv-nav-item ${active ? "active" : ""}`}>
              <Icon size={18} />
              <span>{label}</span>
              {["Leads", "Users", "Reports", "Payments", "Settings"].includes(label) && (
                <ChevronRight className="adv-nav-arrow" size={14} />
              )}
            </div>

            {label === "Advertisements" && (
              <div className="adv-subnav">
                <div>All Advertisements</div>
                <div>Create Advertisement</div>
                <div className="adv-subnav-active">
                  <span />
                  Ad Placements
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="adv-support">
        <div>◉ Need Help?</div>
        <div>
          <MessageCircle size={15} /> Contact Support
        </div>
      </div>

      <div className="adv-collapse">
        <ChevronLeft size={14} />
        Collapse Menu
      </div>
    </aside>
  );
}

function Header() {
  return (
    <div className="adv-top">
      <div>
        <h1>Edit Advertisement</h1>
        <div className="adv-breadcrumb">
          <span>Advertisements</span>
          <ChevronRight size={11} />
          <span>All Advertisements</span>
          <ChevronRight size={11} />
          <span>Edit Advertisement</span>
        </div>
      </div>

      <div className="adv-top-right">
        <div className="adv-search">
          <input placeholder="Search anything..." />
          <Search size={16} />
        </div>

        <div className="adv-notification">
          <Bell size={21} />
          <b>12</b>
        </div>

        <div className="adv-profile">
          <div className="adv-avatar">A</div>
          <div>
            <strong>Admin User</strong>
            <small>Super Admin</small>
          </div>
          <ChevronDown size={15} />
        </div>
      </div>
    </div>
  );
}

function ActionButtons({ review = false, onSaveAndPublish, isSubmitting = false }) {
  const handleClick = (e) => {
    e.preventDefault();
    if (onSaveAndPublish) {
      onSaveAndPublish();
    }
  };

  // return (
  //   <div className="adv-actions">
  //     <button className="adv-outline">
  //       <Eye size={15} /> Preview on Website
  //     </button>
  //     <button className="adv-outline">
  //       <FileCheck size={15} /> Save as Draft
  //     </button>
  //     <button className="adv-primary" onClick={handleClick} disabled={isSubmitting}>
  //       <Send size={15} />
  //       {isSubmitting ? "Saving..." : review ? "Review & Publish" : "Save & Publish"}
  //     </button>
  //   </div>
  // );
}

function Stepper({ currentStep, onStepChange }) {
  return (
    <div className="adv-stepper">
      {steps.map((label, i) => {
        const n = i + 1;

        return (
          <React.Fragment key={label}>
            <button
              type="button"
              className={`adv-step ${n === currentStep ? "current" : ""} ${
                n < currentStep ? "done" : ""
              }`}
              onClick={() => onStepChange(n)}
            >
              <div className="adv-step-circle">{n}</div>
              <span>{label}</span>
            </button>

            {i < steps.length - 1 && (
              <div className={`adv-step-line ${n < currentStep ? "done" : ""}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function DeviceTabs({ device, setDevice }) {
  return (
    <div className="adv-device-tabs">
      <button
        className={device === "desktop" ? "active" : ""}
        onClick={() => setDevice("desktop")}
      >
        <Monitor size={15} /> Desktop
      </button>
      <button
        className={device === "tablet" ? "active" : ""}
        onClick={() => setDevice("tablet")}
      >
        <Tablet size={14} /> Tablet
      </button>
      <button
        className={device === "mobile" ? "active" : ""}
        onClick={() => setDevice("mobile")}
      >
        <Smartphone size={14} /> Mobile
      </button>
    </div>
  );
}

function WebsitePreview({ title = "Advertisement Preview", propertyData = null }) {
  const [device, setDevice] = useState("desktop");

  const propertyName = propertyData?.property_name || "LUXURY LIVING";
  const propertyType = propertyData?.property_type || "Premium Apartments";
  const propertyImage =
    propertyData?.hero_images?.[0]?.image ||
    "https://images.unsplash.com/photo-1600607688969-a5bfcd646154?auto=format&fit=crop&w=1000&q=85";

  return (
    <section className="adv-preview-card">
      <h2>{title}</h2>
      <DeviceTabs device={device} setDevice={setDevice} />

      <div className={`adv-browser ${device}`}>
        <div className="adv-site-header">
          <div className="adv-site-logo">
            <span>♜</span>
            <div>
              <strong>JD/JV</strong>
              <small>PROPERTIES</small>
            </div>
          </div>

          <div className="adv-site-nav">
            <span>Buy</span>
            <span>Rent</span>
            <span>Lease</span>
            <span>JV / JD</span>
            <span>New Launch</span>
            <span>More</span>
          </div>

          <div className="adv-site-icons">♡　♙</div>
        </div>

        <div className="adv-hero-ad">
          <img src={propertyImage} alt={propertyName} />

          <div className="adv-hero-copy">
            <h3>
              {propertyName.toUpperCase()}
              <br />
              REDEFINED
            </h3>
            <p>{propertyType}</p>
            <button>BOOK NOW</button>
          </div>
        </div>

        <div className="adv-launch-title">
          <strong>New Launch Properties</strong>
          <span>View All</span>
        </div>

        <div className="adv-property-row">
          {[
            ["Prestige Waterfront", "₹2.45 Cr* onwards", propertyImages[0]],
            ["Godrej Park Retreat", "₹1.89 Cr* onwards", propertyImages[1]],
            ["Brigade Horizon", "₹1.15 Cr* onwards", propertyImages[2]],
            ["Sattva Aero", "₹1.35 Cr* onwards", propertyImages[3]],
          ].map(([name, price, image]) => (
            <div className="adv-property" key={name}>
              <div className="adv-property-img">
                <img src={image} alt={name} />
                <span>NEW LAUNCH</span>
              </div>
              <strong>{name}</strong>
              <small>Whitefield, Bengaluru</small>
              <b>{price}</b>
              <small>2, 3 & 4 BHK Apartments</small>
            </div>
          ))}
        </div>

        <div className="adv-dots">
          <i className="active" />
          <i />
          <i />
        </div>
      </div>
    </section>
  );
}

/* ---------------- AD PLACEMENTS ---------------- */

const placementGroups = [
  {
    id: "pincoder",
    title: "Playing Add Property Pincoder – Home Page Structure",
    description: "Display your advertisement in property recommendation sections on home page",
    color: "green",
    columns: [
      {
        title: "1. New Launch Properties Page",
        ids: ["new-launch-nearest", "new-launch-similar"],
        labels: ["Nearest Properties", "Similar Properties"],
      },
      {
        title: "2. Premium Properties Page",
        ids: ["premium-nearest", "premium-similar"],
        labels: ["Nearest Properties", "Similar Properties"],
      },
      {
        title: "3. Recommended Properties Page",
        ids: ["recommended-nearest", "recommended-similar"],
        labels: ["Nearest Properties", "Similar Properties"],
      },
      {
        title: "4. Featured Properties Page",
        ids: ["featured-nearest", "featured-similar"],
        labels: ["Nearest Properties", "Similar Properties"],
      },
    ],
  },
  {
    id: "main",
    title: "Main Property Sections",
    description: "Display your advertisement on main property browsing sections",
    color: "blue",
    columns: [
      {
        title: "Buy",
        subtitle: "Buy View List",
        ids: ["buy-view", "buy-list", "buy-description"],
        labels: ["Buy (Buy View List)", "Property List Page", "Property Description Page"],
      },
      {
        title: "Rent",
        subtitle: "Rent View List",
        ids: ["rent-view", "rent-list", "rent-description"],
        labels: ["Rent (Rent View List)", "Property List Page", "Property Description Page"],
      },
      {
        title: "Lease",
        subtitle: "Lease View List",
        ids: ["lease-view", "lease-list", "lease-description"],
        labels: ["Lease (Lease View List)", "Property List Page", "Property Description Page"],
      },
      {
        title: "JV / JD",
        subtitle: "JD View List",
        ids: ["jv-view", "jv-list", "jv-description"],
        labels: ["JV / JD (JD View List)", "Property List Page", "Property Description Page"],
      },
    ],
  },
  {
    id: "action",
    title: "Action Properties",
    description: "Display your advertisement on auction / action property sections",
    color: "purple",
    columns: [
      {
        title: "Action Properties",
        subtitle: "Auction",
        ids: ["action-auction", "action-list", "action-description"],
        labels: ["Action Properties (Auction)", "Property List Page", "Property Description Page"],
      },
    ],
  },
];

function PlacementCheckbox({ checked, color, onClick }) {
  return (
    <button
      type="button"
      className={`adv-place-check ${checked ? `checked ${color}` : ""}`}
      onClick={onClick}
    >
      {checked ? "✓" : ""}
    </button>
  );
}

function PlacementsPage({
  onNext,
  onSaveAndPublish,
  propertyData,
  selectedPlacements,
  setSelectedPlacements,
  isSubmitting,
}) {
  const allIds = useMemo(
    () => placementGroups.flatMap((g) => g.columns.flatMap((c) => c.ids)),
    []
  );

  const [selected, setSelected] = useState(() => {
    if (propertyData?.displaying_pannels && propertyData.displaying_pannels.length > 0) {
      const initialSelected = new Set();
      Object.entries(placementIdToPanelName).forEach(([id, panelName]) => {
        if (propertyData.displaying_pannels.includes(panelName)) {
          initialSelected.add(id);
        }
      });
      return initialSelected;
    }
    return new Set(allIds);
  });

  useEffect(() => {
    setSelectedPlacements(selected);
  }, [selected, setSelectedPlacements]);

  const total = selected.size;

  const toggleId = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleIds = (ids) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));

      ids.forEach((id) => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });

      return next;
    });
  };

  const allSelected = selected.size === allIds.length;

  const groupCount = (group) =>
    group.columns.flatMap((c) => c.ids).filter((id) => selected.has(id)).length;

  const getDisplayPanels = () => {
    const panels = [];
    selected.forEach((id) => {
      if (placementIdToPanelName[id]) {
        panels.push(placementIdToPanelName[id]);
      }
    });
    return panels;
  };

  const handleSaveAndPublish = () => {
    const panels = getDisplayPanels();
    onSaveAndPublish(panels);
  };

  return (
    <>
      <Header />
      <ActionButtons
        review={false}
        onSaveAndPublish={handleSaveAndPublish}
        isSubmitting={isSubmitting}
      />
      <Stepper currentStep={4} onStepChange={onNext} />

      <div className="adv-placement-layout">
        <main className="adv-placement-main">
          <section className="adv-placement-card">
            <div className="adv-placement-header">
              <div>
                <h2>Select Advertisement Display Places</h2>
                <p>Choose where your advertisement will be displayed on the platform</p>
              </div>

              <div className="adv-place-controls">
                <button onClick={() => toggleIds(allIds)}>Select All</button>

                <button
                  className={`adv-switch ${allSelected ? "on" : ""}`}
                  onClick={() => toggleIds(allIds)}
                >
                  <span />
                </button>

                <div className="adv-place-legend">
                  <span>
                    <i className="selected" /> Selected
                  </span>
                  <span>
                    <i className="available" /> Available
                  </span>
                  <span>
                    <i className="unavailable" /> Not Available
                  </span>
                </div>
              </div>
            </div>

            {placementGroups.map((group) => (
              <div className={`adv-placement-group ${group.color}`} key={group.id}>
                <div className="adv-group-heading">
                  <PlacementCheckbox
                    checked={groupCount(group) === group.columns.flatMap((c) => c.ids).length}
                    color={group.color}
                    onClick={() => toggleIds(group.columns.flatMap((c) => c.ids))}
                  />

                  <div>
                    <h3>{group.title}</h3>
                    <p>{group.description}</p>
                  </div>
                </div>

                <div
                  className={`adv-place-columns ${
                    group.id === "action" ? "action-columns" : ""
                  }`}
                >
                  {group.columns.map((column) => (
                    <div className="adv-place-column" key={column.title}>
                      <div className="adv-place-column-title">
                        <PlacementCheckbox
                          checked={column.ids.every((id) => selected.has(id))}
                          color={group.color}
                          onClick={() => toggleIds(column.ids)}
                        />
                        <span>
                          {column.title}
                          {column.subtitle && <small> ({column.subtitle})</small>}
                        </span>
                      </div>

                      {column.ids.map((id, index) => (
                        <div className="adv-place-row" key={id}>
                          <PlacementCheckbox
                            checked={selected.has(id)}
                            color={group.color}
                            onClick={() => toggleId(id)}
                          />
                          <span>{column.labels[index]}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>

          <div className="adv-placement-footer">
            <button className="adv-bottom-prev">
              <ChevronLeft size={16} /> Previous
            </button>

            <button className="adv-bottom-next" onClick={onNext}>
              Next: Targeting <ChevronRight size={16} />
            </button>
          </div>
        </main>

        <aside className="adv-placement-right">
          <WebsitePreview title="Preview on Website" propertyData={propertyData} />

          <section className="adv-selected-summary">
            <h2>Selected Placements Summary</h2>

            <div className="adv-selected-summary-body">
              <div>
                {placementGroups.map((group) => (
                  <div className="adv-selected-group" key={group.id}>
                    <div>
                      <strong>{group.title}</strong>
                      <b>{groupCount(group)}</b>
                    </div>

                    <p>
                      {group.columns
                        .flatMap((c) => c.labels)
                        .slice(0, 4)
                        .map((label, i) => (
                          <span key={i}>• {label}</span>
                        ))}
                    </p>
                  </div>
                ))}
              </div>

              <div className="adv-selected-total">
                <span>Total Selected</span>
                <strong>{total}</strong>
                <small>placements</small>
              </div>
            </div>

            <p className="adv-summary-note">
              Ads will be shown in selected placements based on priority and availability.
            </p>
          </section>
        </aside>
      </div>
    </>
  );
}

/* ---------------- TARGETING ---------------- */

function TargetingMethod() {
  const [method, setMethod] = useState("radius");

  const methods = [
    ["radius", "Location Radius", "Target users within selected radius"],
    ["cities", "By Cities", "Select multiple cities"],
    ["areas", "By Areas", "Select multiple areas"],
    ["pincodes", "By Pincodes", "Select multiple pincodes"],
  ];

  return (
    <div className="adv-method-box">
      <h3>Targeting Method</h3>

      {methods.map(([id, title, sub]) => (
        <button
          key={id}
          className={`adv-method ${method === id ? "selected" : ""}`}
          onClick={() => setMethod(id)}
        >
          <span className="adv-radio">{method === id ? "●" : ""}</span>
          <div>
            <strong>{title}</strong>
            <small>{sub}</small>
          </div>
        </button>
      ))}
    </div>
  );
}

function MapPanel() {
  return (
    <div className="adv-map-panel">
      <div className="adv-map-toolbar">
        <div className="adv-map-search">
          <input placeholder="Search area, landmark..." />
          <Search size={15} />
        </div>

        <button>
          <Navigation size={14} /> Use My Location
        </button>
      </div>

      <div className="adv-map">
        <iframe
          title="Whitefield map"
          src="https://www.openstreetmap.org/export/embed.html?bbox=77.67%2C12.92%2C77.78%2C13.02&layer=mapnik&marker=12.9698%2C77.7499"
        />

        <div className="adv-radius-circle" />
        <div className="adv-map-pin">
          <MapPin size={29} fill="currentColor" />
        </div>

        <div className="adv-map-zoom">
          <button>+</button>
          <button>−</button>
        </div>

        <div className="adv-map-scale">5 km</div>
      </div>
    </div>
  );
}

function TargetingLeft({ propertyData, radius, setRadius }) {
  const location = propertyData?.city || "Whitefield, Bengaluru, Karnataka";

  return (
    <div className="adv-target-left">
      <TargetingMethod />

      <div className="adv-select-location">
        <h3>Select Location</h3>

        <div className="adv-location-chip">
          <MapPin size={14} />
          <span>{location}</span>
          <X size={13} />
        </div>

        <small>Drag the pin or search to set location</small>
      </div>

      <div className="adv-radius">
        <div className="adv-radius-heading">
          <h3>Radius</h3>
          <strong>{radius} km</strong>
        </div>

        <input
          type="range"
          min="1"
          max="1000"
          value={radius}
          onChange={(e) => setRadius(Number(e.target.value))}
        />

        <div className="adv-range-labels">
          <span>1 km</span>
          <span>10 km</span>
          <span>50 km</span>
          <span>100 km</span>
          <span>250 km</span>
          <span>500 km</span>
          <span>1000 km</span>
        </div>
      </div>

      <div className="adv-location-preview">
        <h3>Location Preview</h3>

        <div>
          <span>
            <MapPin size={14} /> {location}
          </span>
          <small>Radius: {radius} km</small>
        </div>
      </div>
    </div>
  );
}

function CampaignPriority({ priority, setPriority }) {
  return (
    <div className="adv-priority">
      <h3>
        Campaign Priority <span>⌄</span>
      </h3>

      <div className="adv-priority-options">
        {[
          ["standard", "Standard", "Normal visibility"],
          ["high", "High Priority", "Better visibility in feeds"],
          ["premium", "Premium Priority", "Top visibility in all sections"],
        ].map(([id, title, sub]) => (
          <button
            key={id}
            className={priority === id ? "selected" : ""}
            onClick={() => setPriority(id)}
          >
            <span className="adv-radio">{priority === id ? "●" : ""}</span>

            <div>
              <strong>
                {id === "premium" && <Crown size={12} />}
                {title}
              </strong>
              <small>{sub}</small>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function TargetingSummary({ propertyData, radius }) {
  const location = propertyData?.city || "Whitefield, Bengaluru, Karnataka";
  const categoryName = propertyData?.category_name || "N/A";

  return (
    <section className="adv-target-summary">
      <h2>Targeting Summary</h2>

      <div className="adv-summary-line">
        <MapPin size={16} />
        <strong>Location</strong>
        <span>{location}</span>
      </div>

      <div className="adv-summary-line">
        <Navigation size={16} />
        <strong className="underlined">Radius</strong>
        <span>{radius} km</span>
      </div>

      <div className="adv-summary-line">
        <UsersRound size={16} />
        <strong>Estimated Reach</strong>
        <span>2,45,000 - 3,10,000 Users</span>
      </div>

      <div className="adv-summary-line">
        <UserRound size={16} />
        <strong>Age Group</strong>
        <span>18 - 65+</span>
      </div>

      <div className="adv-summary-line">
        <Users size={16} />
        <strong>Gender</strong>
        <span>All</span>
      </div>

      <div className="adv-summary-line">
        <Languages size={16} />
        <strong>Language</strong>
        <span>All</span>
      </div>

      <div className="adv-summary-line">
        <Building2 size={16} />
        <strong>Category</strong>
        <span>{categoryName}</span>
      </div>
    </section>
  );
}

function TargetingPage({
  onNext,
  onPrevious,
  onSaveAndPublish,
  propertyData,
  radius,
  setRadius,
  priority,
  setPriority,
  isSubmitting,
}) {
  const handleSaveAndPublish = () => {
    onSaveAndPublish();
  };

  return (
    <>
      <Header />
      <ActionButtons
        review={false}
        onSaveAndPublish={handleSaveAndPublish}
        isSubmitting={isSubmitting}
      />
      <Stepper currentStep={5} onStepChange={onNext} />

      <div className="adv-target-grid">
        <section className="adv-targeting-card">
          <div className="adv-card-title">
            <h2>Targeting Based on Location Radius</h2>
            <p>Choose your target location, radius and audience to show your advertisement.</p>
          </div>

          <div className="adv-target-body">
            <TargetingLeft propertyData={propertyData} radius={radius} setRadius={setRadius} />
            <MapPanel />
          </div>

          <CampaignPriority priority={priority} setPriority={setPriority} />
        </section>

        <aside className="adv-target-right">
          <WebsitePreview propertyData={propertyData} />
          <TargetingSummary propertyData={propertyData} radius={radius} />
        </aside>
      </div>

      <div className="adv-bottom-actions">
        <button className="adv-bottom-prev" onClick={onPrevious}>
          <ChevronLeft size={16} /> Previous
        </button>

        <button className="adv-bottom-next" onClick={onNext}>
          Next: Review & Publish <ChevronRight size={16} />
        </button>
      </div>
    </>
  );
}

/* ---------------- REVIEW ---------------- */

function DetailCard({ icon: Icon, title, children, onEdit, isEditing, onSave, onCancel }) {
  return (
    <section className="adv-detail-card">
      <div className="adv-detail-heading">
        <div>
          <Icon size={16} />
          <h3>{title}</h3>
        </div>

        {isEditing ? (
          <div className="adv-edit-actions">
            <button className="adv-edit-btn save" onClick={onSave}>
              <Save size={12} /> Save
            </button>
            <button className="adv-edit-btn cancel" onClick={onCancel}>
              <XCircle size={12} /> Cancel
            </button>
          </div>
        ) : (
          <button className="adv-edit-btn" onClick={onEdit}>
            <Edit3 size={12} /> Edit
          </button>
        )}
      </div>

      {children}
    </section>
  );
}

function EditableField({
  label,
  value,
  onChange,
  isEditing,
  type = "text",
  options = [],
  placeholder = "",
}) {
  if (!isEditing) {
    return (
      <div>
        <span>{label}</span>
        <b>{value || "N/A"}</b>
      </div>
    );
  }

  if (type === "select" && options.length > 0) {
    return (
      <div>
        <span>{label}</span>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="adv-edit-select"
        >
          <option value="">Select {label}</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div>
        <span>{label}</span>
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="adv-edit-textarea"
          rows={2}
          placeholder={placeholder || `Enter ${label}`}
        />
      </div>
    );
  }

  if (type === "number") {
    return (
      <div>
        <span>{label}</span>
        <input
          type="number"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="adv-edit-input"
          placeholder={placeholder || `Enter ${label}`}
        />
      </div>
    );
  }

  return (
    <div>
      <span>{label}</span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="adv-edit-input"
        placeholder={placeholder || `Enter ${label}`}
      />
    </div>
  );
}

/* ---------------- LOCATION PICKER (Google Maps) ---------------- */

function LocationPicker({ editData, setEditData }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const initializedRef = useRef(false);

  const [location, setLocation] = useState(() => {
    const lat = parseFloat(editData?.lat);
    const lng = parseFloat(editData?.long);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
    return centerDefault;
  });

  const [locationAddress, setLocationAddress] = useState(editData?.location || "");
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
  const [markerIcon, setMarkerIcon] = useState(null);

  // Only sync on MOUNT
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const lat = parseFloat(editData?.lat);
    const lng = parseFloat(editData?.long);
    if (!isNaN(lat) && !isNaN(lng)) {
      setLocation({ lat, lng });
    }
    if (editData?.location) {
      setLocationAddress(editData.location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize geocoder
  useEffect(() => {
    if (isLoaded && window.google?.maps && !geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  // Set marker icon
  useEffect(() => {
    if (isLoaded && window.google) {
      setMarkerIcon({
        url:
          "data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22%23ff0000%22%20d%3D%22M12%202C8.13%202%205%205.13%205%209c0%205.25%207%2013%207%2013s7-7.75%207-13c0-3.87-3.13-7-7-7zm0%209.5c-1.38%200-2.5-1.12-2.5-2.5s1.12-2.5%202.5-2.5%202.5%201.12%202.5%202.5-1.12%202.5-2.5%202.5z%22%2F%3E%3C%2Fsvg%3E",
        scaledSize: new window.google.maps.Size(40, 40),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(20, 40),
      });
    }
  }, [isLoaded]);

  const updateLocationFromLatLng = (lat, lng) => {
    const finalLat = String(lat);
    const finalLong = String(lng);

    setLocation({ lat, lng });

    if (!geocoderRef.current) {
      const fallback = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
      setLocationAddress(fallback);
      setEditData((prev) => ({
        ...prev,
        location: fallback,
        lat: finalLat,
        long: finalLong,
      }));
      return;
    }

    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      let finalAddress = "";
      if (status === "OK" && results && results[0]) {
        finalAddress = results[0].formatted_address;
      } else {
        finalAddress = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
      }
      setLocationAddress(finalAddress);
      setEditData((prev) => ({
        ...prev,
        location: finalAddress,
        lat: finalLat,
        long: finalLong,
      }));
    });
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by this browser.");
      return;
    }

    setUsingCurrentLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setLocation({ lat, lng });
        updateLocationFromLatLng(lat, lng);
        setLocationConfirmed(true);
        setUsingCurrentLocation(false);
      },
      (error) => {
        console.error("Error getting current location:", error);
        toast.error("Error getting current location. Please enable location services.");
        setUsingCurrentLocation(false);
      }
    );
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const address = place.formatted_address || "";

        setLocation({ lat, lng });
        setLocationAddress(address);
        setLocationConfirmed(true);
        setUsingCurrentLocation(false);

        setEditData((prev) => ({
          ...prev,
          location: address,
          lat: String(lat),
          long: String(lng),
        }));
      }
    }
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setLocationConfirmed(false);
    setUsingCurrentLocation(false);
    updateLocationFromLatLng(lat, lng);
  };

  const confirmLocation = () => {
    if (window.google && window.google.maps && mapRef.current) {
      const center = mapRef.current.getCenter();
      const lat = center.lat();
      const lng = center.lng();
      setLocation({ lat, lng });

      if (!geocoderRef.current) return;

      geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
        let finalAddress = "";
        if (status === "OK" && results && results[0]) {
          finalAddress = results[0].formatted_address;
        } else {
          finalAddress = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
        }
        setLocationAddress(finalAddress);
        setEditData((prev) => ({
          ...prev,
          location: finalAddress,
          lat: String(lat),
          long: String(lng),
        }));
        setLocationConfirmed(true);
      });
    }
  };

  if (!isLoaded && !loadError) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Typography sx={{ textAlign: "center", py: 3, color: "#c62828" }}>
        Error loading Google Maps. Please check your API key.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <GoogleAutocomplete
        onLoad={(autocomplete) => {
          autocompleteRef.current = autocomplete;
        }}
        onPlaceChanged={onPlaceChanged}
      >
        <TextField
          fullWidth
          placeholder="Search location on map"
          value={locationAddress}
          onChange={(e) => setLocationAddress(e.target.value)}
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={getCurrentLocation}
                  edge="end"
                  size="small"
                  disabled={usingCurrentLocation}
                >
                  {usingCurrentLocation ? (
                    <CircularProgress size={18} />
                  ) : (
                    <MyLocation sx={{ color: "#9a4f20" }} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#FFFFFF",
              fontSize: "13px",
              "& fieldset": { borderColor: "#E0E0E0" },
              "&:hover fieldset": { borderColor: "#BDBDBD" },
              "&.Mui-focused fieldset": { borderColor: "#9a4f20" },
            },
          }}
        />
      </GoogleAutocomplete>

      <div style={{ ...containerStyle, position: "relative" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={location}
          zoom={15}
          options={{ gestureHandling: "greedy" }}
          onLoad={(map) => {
            mapRef.current = map;
          }}
          onClick={handleMapClick}
          onDragStart={() => {
            setLocationConfirmed(false);
          }}
        >
          {markerIcon && <Marker position={location} icon={markerIcon} />}
        </GoogleMap>

        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            viewBox="0 0 24 24"
          >
            <path
              fill="#ff0000"
              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
            />
          </svg>
        </div>

        <button
          type="button"
          onClick={confirmLocation}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: locationConfirmed ? "green" : "red",
            border: "none",
            borderRadius: "50%",
            width: "44px",
            height: "44px",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
            position: "absolute",
            right: "10px",
            top: "45%",
            transform: "translateY(-50%)",
            zIndex: 10,
            transition: "background-color 0.3s ease",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="26"
            viewBox="0 0 24 24"
          >
            <path
              fill="white"
              d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
            />
          </svg>
        </button>
      </div>

      {locationAddress && (
        <Typography sx={{ fontSize: "11px", color: "#666", mt: 1 }}>
          📍 {locationAddress}
        </Typography>
      )}
    </Box>
  );
}

/* ---------------- AD DETAILS CARD ---------------- */

function AdDetailsCard({
  propertyData,
  isEditing,
  editData,
  setEditData,
  onEdit,
  onSave,
  onCancel,
}) {
  if (!propertyData) return null;

  const handleChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <DetailCard
      icon={FileText}
      title="Ad Details"
      onEdit={onEdit}
      isEditing={isEditing}
      onSave={onSave}
      onCancel={onCancel}
    >
      <div className="adv-details-grid">
        <EditableField
          label="Property Name"
          value={editData?.property_name}
          onChange={(v) => handleChange("property_name", v)}
          isEditing={isEditing}
        />
        <EditableField
          label="Category"
          value={editData?.category_name}
          onChange={(v) => handleChange("category_name", v)}
          isEditing={isEditing}
        />
        <EditableField
          label="Property Type"
          value={editData?.property_type}
          onChange={(v) => handleChange("property_type", v)}
          isEditing={isEditing}
          type="select"
          options={["Apartment", "Independent House", "Villa", "Commercial", "Land/Plot"]}
        />
        <EditableField
          label="City"
          value={editData?.city}
          onChange={(v) => handleChange("city", v)}
          isEditing={isEditing}
        />
        <EditableField
          label="Address"
          value={editData?.address}
          onChange={(v) => handleChange("address", v)}
          isEditing={isEditing}
          type="textarea"
        />
        <EditableField
          label="Landmark"
          value={editData?.landmark}
          onChange={(v) => handleChange("landmark", v)}
          isEditing={isEditing}
        />
        <EditableField
          label="Status"
          value={editData?.status ? "Active" : "Inactive"}
          onChange={(v) => handleChange("status", v === "Active")}
          isEditing={isEditing}
          type="select"
          options={["Active", "Inactive"]}
        />
        <EditableField
          label="Possession Date"
          value={editData?.possession_date}
          onChange={(v) => handleChange("possession_date", v)}
          isEditing={isEditing}
          type="text"
          placeholder="YYYY-MM-DD"
        />
        <EditableField
          label="Tags"
          value={editData?.tags}
          onChange={(v) => handleChange("tags", v)}
          isEditing={isEditing}
        />
        <EditableField
          label="Description"
          value={editData?.description}
          onChange={(v) => handleChange("description", v)}
          isEditing={isEditing}
          type="textarea"
        />
      </div>

      {isEditing && (
        <Box sx={{ mt: 3 }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#1A1A1A",
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <MapPin size={14} /> Select Location on Map
          </Typography>
          <LocationPicker editData={editData} setEditData={setEditData} />
        </Box>
      )}
    </DetailCard>
  );
}

function CampaignDetailsCard({
  propertyData,
  isEditing,
  editData,
  setEditData,
  onEdit,
  onSave,
  onCancel,
}) {
  if (!propertyData) return null;

  const handleChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <DetailCard
      icon={CalendarDays}
      title="Campaign Details"
      onEdit={onEdit}
      isEditing={isEditing}
      onSave={onSave}
      onCancel={onCancel}
    >
      <div className="adv-details-grid campaign">
        <EditableField
          label="Start Date"
          value={editData?.adstart_date ? editData.adstart_date.split("T")[0] : ""}
          onChange={(v) => handleChange("adstart_date", v)}
          isEditing={isEditing}
          type="text"
          placeholder="YYYY-MM-DD"
        />
        <EditableField
          label="Status"
          value={editData?.status ? "Active" : "Inactive"}
          onChange={(v) => handleChange("status", v === "Active")}
          isEditing={isEditing}
          type="select"
          options={["Active", "Inactive"]}
        />
        <EditableField
          label="End Date"
          value={editData?.adend_date ? editData.adend_date.split("T")[0] : ""}
          onChange={(v) => handleChange("adend_date", v)}
          isEditing={isEditing}
          type="text"
          placeholder="YYYY-MM-DD"
        />
        <EditableField
          label="Boost Date"
          value={editData?.boost_date ? editData.boost_date.split("T")[0] : ""}
          onChange={(v) => handleChange("boost_date", v)}
          isEditing={isEditing}
          type="text"
          placeholder="YYYY-MM-DD"
        />
      </div>
    </DetailCard>
  );
}

function ReviewPage({
  onPrevious,
  onSaveAndPublish,
  propertyData,
  isSubmitting,
  onDataChange,
}) {
  const [editingCard, setEditingCard] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (propertyData) {
      setEditData({ ...propertyData });
    }
  }, [propertyData]);

  const handleEdit = (cardName) => {
    setEditingCard(cardName);
    setEditData({ ...propertyData });
  };

  const handleSave = (cardName) => {
    const finalData = {
      ...editData,
      lat: editData?.lat ?? propertyData?.lat ?? null,
      long: editData?.long ?? propertyData?.long ?? null,
      location: editData?.location ?? propertyData?.location ?? null,
    };

    console.log(`✅ Saving ${cardName}:`, {
      lat: finalData.lat,
      long: finalData.long,
      location: finalData.location,
    });

    onDataChange(finalData);
    setEditingCard(null);
    toast.success(`${cardName} updated successfully!`);
  };

  const handleCancel = () => {
    setEditingCard(null);
    setEditData({ ...propertyData });
  };

  if (!propertyData) {
    return (
      <div className="adv-loading">
        <div className="adv-loader">Loading property data...</div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <ActionButtons
        review={true}
        onSaveAndPublish={() => {
          if (editingCard) {
            const finalData = {
              ...editData,
              lat: editData?.lat ?? propertyData?.lat ?? null,
              long: editData?.long ?? propertyData?.long ?? null,
              location: editData?.location ?? propertyData?.location ?? null,
            };
            onDataChange(finalData);
            setEditingCard(null);
          }
          setTimeout(() => {
            onSaveAndPublish();
          }, 0);
        }}
        isSubmitting={isSubmitting}
      />
      <Stepper currentStep={6} onStepChange={() => {}} />

      <div className="adv-review-grid">
        <main className="adv-review-main">
          <div className="adv-review-title">
            <h2>Review Advertisement Details</h2>
            <p>
              Please review all details before publishing your advertisement. Click Edit to
              make changes.
            </p>
          </div>

          <div className="adv-review-columns">
            <div className="adv-review-left">
              <AdDetailsCard
                propertyData={propertyData}
                isEditing={editingCard === "Ad Details"}
                editData={editData}
                setEditData={setEditData}
                onEdit={() => handleEdit("Ad Details")}
                onSave={() => handleSave("Ad Details")}
                onCancel={handleCancel}
              />

              <CampaignDetailsCard
                propertyData={propertyData}
                isEditing={editingCard === "Campaign Details"}
                editData={editData}
                setEditData={setEditData}
                onEdit={() => handleEdit("Campaign Details")}
                onSave={() => handleSave("Campaign Details")}
                onCancel={handleCancel}
              />

              <DetailCard icon={Target} title="Targeting Summary">
                <div className="adv-target-detail-grid">
                  <div>
                    <span>Center Location</span>
                    <b>{propertyData.city || "N/A"}</b>
                  </div>
                  <div>
                    <span>Address</span>
                    <b>{propertyData.address || "N/A"}</b>
                  </div>
                  <div>
                    <span>Location</span>
                    <b>{propertyData.location || "N/A"}</b>
                  </div>
                  <div>
                    <span>Latitude</span>
                    <b>{propertyData.lat || "N/A"}</b>
                  </div>
                  <div>
                    <span>Longitude</span>
                    <b>{propertyData.long || "N/A"}</b>
                  </div>
                  <div>
                    <span>Category</span>
                    <b>{propertyData.category_name || "N/A"}</b>
                  </div>
                  <div>
                    <span>Property Type</span>
                    <b>{propertyData.property_type || "N/A"}</b>
                  </div>
                  <div>
                    <span>Radius</span>
                    <b>{propertyData.radius_distance || "10"} km</b>
                  </div>
                  <div>
                    <span>Campaign Priority</span>
                    <b>
                      {propertyData.priority
                        ? propertyData.priority.toUpperCase()
                        : "N/A"}
                    </b>
                  </div>
                  <div>
                    <span>Target Audience Estimate</span>
                    <b>2,45,000 - 3,10,000 Users</b>
                  </div>
                </div>
              </DetailCard>

              <DetailCard icon={Target} title="Selected Ad Placements">
                <div className="adv-placement-summary">
                  <div className="adv-placement-list">
                    {propertyData.displaying_pannels?.length > 0 ? (
                      propertyData.displaying_pannels.map((panel, index) => (
                        <div key={index}>
                          {panel.replace(/_/g, " ").toUpperCase()}{" "}
                          <CheckCircle2 size={14} />
                        </div>
                      ))
                    ) : (
                      <div>No placements selected</div>
                    )}
                  </div>

                  <div className="adv-total-placement">
                    <span>Total Selected Placements</span>
                    <strong>{propertyData.displaying_pannels?.length || 0}</strong>
                    <small>places</small>
                  </div>
                </div>
              </DetailCard>

              {propertyData.location_advantages?.length > 0 && (
                <DetailCard icon={MapPin} title="Location Advantages">
                  <div className="adv-location-advantages">
                    {propertyData.location_advantages.map((advantage) => (
                      <div className="adv-advantage-item" key={advantage.id}>
                        <strong>{advantage.place_name}</strong>
                        <span>Distance: {advantage.distance}</span>
                        <span>Travel Time: {advantage.travel_time}</span>
                      </div>
                    ))}
                  </div>
                </DetailCard>
              )}

              {propertyData.specifications?.length > 0 && (
                <DetailCard icon={FileText} title="Specifications">
                  <div className="adv-specs-grid">
                    {propertyData.specifications.map((spec) => (
                      <div className="adv-spec-item" key={spec.id}>
                        <strong>{spec.name}</strong>
                        <span>{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </DetailCard>
              )}
            </div>

            <div className="adv-review-middle">
              <DetailCard icon={Building2} title="Configurations">
                {propertyData.configurations?.length > 0 ? (
                  <div className="adv-config-grid">
                    {propertyData.configurations.map((config) => (
                      <div className="adv-config-item" key={config.id}>
                        <div>
                          <strong>{config.bhk_type}</strong>
                          <span>₹{config.price?.toLocaleString() || "N/A"}</span>
                        </div>
                        <div className="adv-config-details">
                          <span>Carpet: {config.carpet_area} sq.ft</span>
                          <span>Built-up: {config.built_up_area} sq.ft</span>
                          <span>Bathrooms: {config.bathrooms}</span>
                          <span>Balcony: {config.balcony}</span>
                          <span>Facing: {config.facing || "N/A"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="adv-no-data">No configurations available</div>
                )}
              </DetailCard>

              <DetailCard icon={BarChart4} title="Advertisement Performance (Live)">
                <div className="adv-performance-grid">
                  {[
                    ["Total Views", "125,430"],
                    ["Total Clicks", "3,240"],
                    ["CTR", "2.58%"],
                    ["Call Clicks", "1,245"],
                    ["WhatsApp Clicks", "856"],
                    ["Property Clicks", "642"],
                    ["Website Clicks", "321"],
                    ["Share Clicks", "176"],
                    ["Favorites", "543"],
                  ].map(([label, value]) => (
                    <div className="adv-stat" key={label}>
                      <div>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </DetailCard>

              <DetailCard icon={UsersRound} title="Lead Management (Live)">
                <div className="adv-lead-wrap">
                  <div className="adv-total-leads">
                    <span>Total Leads</span>
                    <strong>{Math.floor(Math.random() * 200) + 50}</strong>
                  </div>

                  <div className="adv-leads-table">
                    <div className="adv-lead-table-head">
                      <strong>Recent Leads</strong>
                      <a>View All Leads</a>
                    </div>

                    <div className="adv-lead-head-row">
                      <span>Lead Name</span>
                      <span>Mobile Number</span>
                      <span>Date</span>
                      <span>Status</span>
                    </div>

                    {[
                      ["Rohit Sharma", "+91 98765 43210", formatDate(new Date()), "New"],
                      ["Priya Nair", "+91 87654 32109", formatDate(new Date()), "Contacted"],
                      ["Amit Verma", "+91 76543 21098", formatDate(new Date()), "Follow-up"],
                    ].map(([name, phone, date, status]) => (
                      <div className="adv-lead-row" key={name}>
                        <span>{name}</span>
                        <span>{phone}</span>
                        <span>{date}</span>
                        <span
                          className={`status-${status.toLowerCase().replace("-", "")}`}
                        >
                          {status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </DetailCard>

              <DetailCard icon={ImageIcon} title="Campaign Preview">
                <div className="adv-campaign-preview">
                  <img
                    src={
                      propertyData.hero_images?.[0]?.image ||
                      "https://images.unsplash.com/photo-1600607688969-a5bfcd646154?auto=format&fit=crop&w=1000&q=85"
                    }
                    alt={propertyData.property_name}
                  />
                  <div>
                    <h3>
                      {propertyData.property_name?.toUpperCase() || "LUXURY LIVING"}
                      <br />
                      REDEFINED
                    </h3>
                    <p>{propertyData.property_type || "Premium Apartments"}</p>
                    <button>BOOK NOW</button>
                  </div>
                </div>
              </DetailCard>
            </div>
          </div>
        </main>

        <aside className="adv-review-right">
          <WebsitePreview title="Advertisement Preview" propertyData={propertyData} />

          <section className="adv-publish-summary">
            <h2>Publishing Summary</h2>

            {[
              ["Everything looks good!", "Your advertisement is ready to be published."],
              ["Property Name", propertyData.property_name || "N/A"],
              ["Property Type", propertyData.property_type || "N/A"],
              ["Category", propertyData.category_name || "N/A"],
              ["City", propertyData.city || "N/A"],
              ["Status", propertyData.status ? "Active" : "Inactive"],
              ["Created At", formatDate(propertyData.created_at)],
              ["Possession Date", formatDate(propertyData.possession_date)],
              ["Configurations", `${propertyData.configurations?.length || 0} BHK types`],
              ["Hero Images", `${propertyData.hero_images?.length || 0} images`],
              ["Builder", propertyData.builder_details?.builder_name || "N/A"],
            ].map(([title, value], i) => {
              const isFirst = i === 0;
              const isStatus = title === "Status";

              return (
                <div className="adv-publish-row" key={title}>
                  <div className="adv-publish-icon">
                    {isFirst ? <CheckCircle2 size={16} /> : <FileText size={16} />}
                  </div>

                  <div>
                    <strong>{title}</strong>
                    <span
                      className={isStatus && value === "Active" ? "adv-green-text" : ""}
                    >
                      {value}
                    </span>
                  </div>

                  {!isFirst && <CheckCircle2 className="adv-publish-check" size={15} />}
                </div>
              );
            })}
          </section>
        </aside>
      </div>

      <div className="adv-review-footer">
        <button className="adv-bottom-prev" onClick={onPrevious}>
          <ChevronLeft size={16} /> Previous
        </button>

        <div>
          <button className="adv-save-final">
            <FileCheck size={15} /> Save as Draft
          </button>

          <button
            className="adv-publish-final"
            onClick={() => {
              if (editingCard) {
                const finalData = {
                  ...editData,
                  lat: editData?.lat ?? propertyData?.lat ?? null,
                  long: editData?.long ?? propertyData?.long ?? null,
                  location: editData?.location ?? propertyData?.location ?? null,
                };
                onDataChange(finalData);
                setEditingCard(null);
              }
              setTimeout(() => {
                onSaveAndPublish();
              }, 0);
            }}
          >
            <Send size={15} /> Publish Advertisement <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </>
  );
}

/* ---------------- SINGLE PAGE WIZARD ---------------- */

export default function EditAdvertisement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(4);
  const [propertyData, setPropertyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlacements, setSelectedPlacements] = useState(new Set());
  const [radius, setRadius] = useState(10);

  // Priority (UI value: standard | high | premium)
  const [priority, setPriorityRaw] = useState("high");
  const priorityRef = useRef("high");

  const setPriority = useCallback((value) => {
    priorityRef.current = value;
    setPriorityRaw(value);
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const latestDataRef = useRef(null);

  // Fetch property details by ID
  useEffect(() => {
    const fetchPropertyDetails = async () => {
      if (!id) {
        toast.error("Property ID not found");
        navigate("/admin-ad-property");
        return;
      }

      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/ad-properties/combined/${id}/`);

        if (response.data?.success && response.data?.data) {
          setPropertyData(response.data.data);
          latestDataRef.current = response.data.data;
          if (response.data.data.radius_distance) {
            setRadius(parseInt(response.data.data.radius_distance));
          }
          // Initialize priority from backend value
          if (response.data.data.priority) {
            const mapped =
              PRIORITY_FROM_BACKEND[response.data.data.priority] || "high";
            priorityRef.current = mapped;
            setPriorityRaw(mapped);
          }
        } else {
          toast.error("Failed to fetch property details");
        }
      } catch (error) {
        console.error("Error fetching property details:", error);
        toast.error("Error loading property details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPropertyDetails();
  }, [id, navigate]);

  const updatePropertyData = (updatedData) => {
    const merged = {
      ...(latestDataRef.current || propertyData || {}),
      ...updatedData,
    };

    latestDataRef.current = merged;
    setPropertyData(merged);
  };

  const buildCompletePayload = (panels) => {
    const source = latestDataRef.current || propertyData;
    if (!source) return null;

    const payload = JSON.parse(JSON.stringify(source));

    payload.location = source.location ?? null;
    payload.lat = source.lat ?? null;
    payload.long = source.long ?? null;

    payload.displaying_pannels = panels || [];
    payload.radius_distance = String(radius);
    payload.radius_distance_units = "km";

    // Map UI priority -> backend priority
    payload.priority = PRIORITY_TO_BACKEND[priorityRef.current] || "low";

    console.log("📤 Final payload fields:", {
      location: payload.location,
      lat: payload.lat,
      long: payload.long,
      radius_distance: payload.radius_distance,
      priority: payload.priority,
    });

    return payload;
  };

  const handleSaveAndPublish = (panelsFromPlacements) => {
    console.log("========== HANDLE SAVE & PUBLISH START ==========");

    console.log("📥 panelsFromPlacements:", panelsFromPlacements);
    console.log("📌 selectedPlacements:", selectedPlacements);
    console.log("🗺️ placementIdToPanelName:", placementIdToPanelName);

    let panels = panelsFromPlacements;

    if (!panels || panels.length === 0) {
      console.log(
        "⚠️ No panelsFromPlacements received. Building panels from selectedPlacements..."
      );

      panels = [];

      selectedPlacements.forEach((id) => {
        console.log("🔍 Checking placement ID:", id);
        console.log("➡️ Panel name:", placementIdToPanelName[id]);

        if (placementIdToPanelName[id]) {
          panels.push(placementIdToPanelName[id]);
        }
      });
    }

    if (!panels) {
      console.log("⚠️ panels is null/undefined. Setting to empty array.");
      panels = [];
    }

    console.log("✅ Final panels:", panels);

    console.log("🔨 Building complete payload...");

    const payload = buildCompletePayload(panels);

    console.log("📦 Generated payload:", payload);

    if (!payload) {
      console.error("❌ Failed to build payload");
      toast.error("Failed to build payload");
      return;
    }

    console.log("📤 Final payload that will be sent to API:");
    console.log(JSON.stringify(payload, null, 2));

    console.log("🌐 API URL:", `${BASE_URL}/ad-properties/combined/${id}/`);

    performUpdate(payload);

    console.log("========== HANDLE SAVE & PUBLISH END ==========");
  };

  const performUpdate = async (payload) => {
    console.log("========== PERFORM UPDATE START ==========");

    try {
      setIsSubmitting(true);

      const apiUrl = `${BASE_URL}/ad-properties/combined/${id}/`;

      console.log("🚀 Starting API PUT request");
      console.log("🌐 URL:", apiUrl);
      console.log("📌 ID:", id);
      console.log("📦 Request payload:");
      console.log(JSON.stringify(payload, null, 2));

      console.log("📋 Request headers:", {
        "Content-Type": "application/json",
      });

      const response = await axios.put(apiUrl, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("✅ API request completed");
      console.log("📊 Response status:", response.status);
      console.log("📊 Response status text:", response.statusText);
      console.log("📥 API response:");
      console.log(JSON.stringify(response.data, null, 2));

      if (response.data?.success) {
        console.log("🎉 Property updated successfully!");

        toast.success("Property updated successfully!");

        setPropertyData((prev) => {
          const updatedData = {
            ...prev,
            ...payload,
          };

          console.log("🔄 Updated propertyData:", updatedData);

          return updatedData;
        });

        latestDataRef.current = {
          ...(latestDataRef.current || {}),
          ...payload,
        };

        console.log("💾 latestDataRef.current updated:", latestDataRef.current);

        console.log("➡️ Navigating to /admin-ad-property in 1500ms...");

        setTimeout(() => {
          console.log("🔀 Navigating to /admin-ad-property");
          navigate("/admin-ad-property");
        }, 1500);
      } else {
        console.error("❌ API returned success=false:", response.data);

        toast.error(response.data?.message || "Failed to update property");
      }
    } catch (error) {
      console.error("❌ Error updating property:", error);

      if (error.response) {
        console.error("📛 Server responded with error");
        console.error("📊 Status:", error.response.status);
        console.error("📊 Status Text:", error.response.statusText);
        console.error("📥 Error response data:");
        console.error(JSON.stringify(error.response.data, null, 2));

        toast.error(
          error.response.data?.message || `Error ${error.response.status}`
        );
      } else if (error.request) {
        console.error(
          "📡 Request was sent but no response received:",
          error.request
        );

        toast.error("No response from server. Please check your connection.");
      } else {
        console.error("⚠️ Error while creating request:", error.message);

        toast.error("Error updating property: " + error.message);
      }
    } finally {
      console.log("🔚 Setting isSubmitting to false");
      setIsSubmitting(false);

      console.log("========== PERFORM UPDATE END ==========");
    }
  };

  const goToStep = (step) => {
    if (step >= 4 && step <= 6) {
      setCurrentStep(step);
    }
  };

  if (isLoading) {
    return (
      <div className="adv-app">
        <Sidebar />
        <main className="adv-main">
          <div className="adv-loading-container">
            <div className="adv-loader-spinner"></div>
            <p>Loading property details...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!propertyData) {
    return (
      <div className="adv-app">
        <Sidebar />
        <main className="adv-main">
          <div className="adv-error-container">
            <p>Property not found</p>
            <button onClick={() => navigate("/admin-ad-property")}>
              Go Back to Properties
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="adv-app">
      <Sidebar />

      <main className="adv-main">
        {currentStep === 4 && (
          <PlacementsPage
            onNext={() => setCurrentStep(5)}
            onSaveAndPublish={handleSaveAndPublish}
            propertyData={propertyData}
            selectedPlacements={selectedPlacements}
            setSelectedPlacements={setSelectedPlacements}
            isSubmitting={isSubmitting}
          />
        )}

        {currentStep === 5 && (
          <TargetingPage
            onNext={() => setCurrentStep(6)}
            onPrevious={() => setCurrentStep(4)}
            onSaveAndPublish={() => handleSaveAndPublish()}
            propertyData={propertyData}
            radius={radius}
            setRadius={setRadius}
            priority={priority}
            setPriority={setPriority}
            isSubmitting={isSubmitting}
          />
        )}

        {currentStep === 6 && (
          <ReviewPage
            onPrevious={() => setCurrentStep(5)}
            onSaveAndPublish={() => handleSaveAndPublish()}
            propertyData={propertyData}
            isSubmitting={isSubmitting}
            onDataChange={updatePropertyData}
          />
        )}
      </main>
    </div>
  );
}